import { Alert, Badge, Button, Card, Group, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useBusinessDate, useLocationId } from '../../hooks/useSeedLocation';
import { formatDate, formatFxRate, formatMoney, formatRateBp } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

/**
 * The calculator, replaced.
 *
 * The owner used to read a night's takings, work out ten percent of a table by hand and type the
 * figure into a payout — the step that stops happening in a busy week and the step a driver
 * disputes. This screen works it out from the bills themselves and, before writing anything, shows
 * what it worked it out FROM: which night, whose table, which bill, what the base was and what rate
 * was applied.
 *
 * Two buttons, deliberately. "Work it out" writes nothing at all, and it is the one that gets
 * pressed while somebody is standing at the bar asking. "Save these figures" is a separate decision,
 * and even then nobody has been paid — the money is handed over from Payouts, as it always was.
 */

const COMPUTE_PATH = '/admin/v1/referrals/accruals/compute';

interface Working {
  profileId: string;
  profileName: string;
  level: 'driver' | 'agency';
  planName: string;
  businessDate: string;
  tabName: string | null;
  orderNumber: number | null;
  checkId: string;
  checkNumber: number;
  basis: 'preTaxNet' | 'gross';
  baseMinor: number;
  baseCurrency: string;
  rateBp: number;
  amountMinor: number;
  currency: string;
  fxRateMicro: number | null;
  tierApplied: boolean;
}

interface Skip {
  orderId: string;
  tabName: string | null;
  businessDate: string;
  profileName: string;
  reason:
    | 'noPlan'
    | 'noSettledBill'
    | 'houseTab'
    | 'nothingToPayOn'
    | 'unknownPartner'
    | 'onAPayoutList'
    | 'noExchangeRate'
    | 'planIncomplete'
    | 'billWouldNotAddUp';
}

interface Run {
  from: string;
  to: string;
  committed: boolean;
  workings: Working[];
  skipped: Skip[];
  totals: { profileId: string; profileName: string; currency: string; amountMinor: number }[];
}

/**
 * Why a tagged table earned nothing, in the words the owner would use to fix it.
 *
 * Every one of these is actionable, which is the reason they are shown at all: a partner who earns
 * nothing because nobody put him on a plan looks exactly like a partner who brought nobody in, and
 * the difference is a fortnight of unpaid commission.
 */
const WHY: Record<Skip['reason'], string> = {
  noPlan: 'Not on a commission plan yet — put them on one and work it out again',
  noSettledBill: 'Nothing on this tab has been paid yet — a share is earned when the guest pays',
  houseTab: 'This was on the house, so there is nothing anybody collected to take a share of',
  nothingToPayOn: 'The bills were paid and came to nothing to take a share of',
  unknownPartner: 'The tag on this tab does not match anybody — put it right and work it out again',
  // Deliberately not "already paid": a run claims what it gathers when it is drawn up, and the
  // owner reading that a driver has his money while the envelope is still on the desk is the exact
  // sentence this wording exists to stop.
  onAPayoutList:
    'Already on a payout list, so it was left alone — the payouts screen says whether it has been handed over',
  noExchangeRate: 'The bill carries no exchange rate, so it cannot be turned into the other currency',
  planIncomplete: 'Their plan does not say what they earn — set a share or an amount on it',
  billWouldNotAddUp: 'What was taken off this bill comes to more than the bill, so it cannot be divided',
};

const BASIS: Record<Working['basis'], string> = {
  preTaxNet: 'the bill before tax',
  gross: 'the bill including tax',
};

export function WorkOutCommissionSection() {
  const locationId = useLocationId();
  const today = useBusinessDate();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [run, setRun] = useState<Run | null>(null);
  const [busy, setBusy] = useState(false);

  // Both boxes default to the trading day at the bar, which is the overwhelmingly common ask —
  // "what did the drivers earn tonight" — and never to the browser's idea of today.
  const first = from || today || '';
  const last = to || today || '';

  const send = async (commit: boolean) => {
    if (!locationId) return;
    setBusy(true);
    try {
      const result = await rawApi<Run>('POST', COMPUTE_PATH, {
        locationId,
        from: first,
        to: last,
        commit,
      });
      setRun(result);
      if (commit) {
        notifyDone(
          'Commission saved',
          result.workings.length === 0
            ? 'Nothing was earned over those nights.'
            : 'It is now on "What we owe". Nobody has been paid — hand the money over from Payouts.',
        );
        void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/referrals/balances') });
        void queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/referrals/accruals') });
      }
    } catch (err) {
      notifyError(err, commit ? 'save these figures' : 'work out the commission');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>Work out what the partners earned</Title>
          <Text size="sm" c="dimmed" mt={4}>
            Works the commission out from the bills themselves, so nobody has to do it on a calculator. It
            shows you what each figure came from before anything is saved, and saving it does not pay anybody
            — that still happens from Payouts.
          </Text>
        </div>

        <Group align="flex-end" gap="sm">
          <TextInput
            type="date"
            label="From the night of"
            value={first}
            onChange={(e) => setFrom(e.currentTarget.value)}
          />
          <TextInput
            type="date"
            label="To the night of"
            value={last}
            onChange={(e) => setTo(e.currentTarget.value)}
          />
          <Button variant="light" loading={busy} onClick={() => void send(false)} disabled={!locationId}>
            Work it out
          </Button>
          {run && run.workings.length > 0 && !run.committed && (
            <Button loading={busy} onClick={() => void send(true)}>
              Save these figures
            </Button>
          )}
        </Group>

        {/*
          A night runs to four in the morning, and the difference matters to a driver: a table he
          brought in at eleven that paid at half past one is one night's work, not two.
        */}
        <Text size="xs" c="dimmed">
          A night runs until 4am, and a table counts on the night it sat down.
        </Text>

        {run && (
          <Stack gap="sm">
            {run.totals.length > 0 && (
              <Group gap="xs">
                {run.totals.map((t) => (
                  <Badge key={`${t.profileId}-${t.currency}`} size="lg" variant="light" color="teal">
                    {t.profileName}: {formatMoney(t.amountMinor, t.currency)}
                  </Badge>
                ))}
              </Group>
            )}

            {run.workings.length === 0 && run.skipped.length === 0 && (
              <Text size="sm" c="dimmed">
                No tables were put down to a partner over{' '}
                {run.from === run.to
                  ? `the night of ${formatDate(run.from)}`
                  : `${formatDate(run.from)} – ${formatDate(run.to)}`}
                .
              </Text>
            )}

            {run.workings.length > 0 && (
              <Table.ScrollContainer minWidth={720}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Partner</Table.Th>
                      <Table.Th>Night</Table.Th>
                      <Table.Th>Table</Table.Th>
                      <Table.Th>Worked out on</Table.Th>
                      <Table.Th>Share</Table.Th>
                      <Table.Th ta="right">Commission</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {run.workings.map((w) => (
                      // One partner earns at most one line per bill per level, which is what the
                      // ledger's own unique index says — so this is a key and not a guess.
                      <Table.Tr key={`${w.profileId}-${w.level}-${w.checkId}`}>
                        <Table.Td>
                          {w.profileName}
                          {w.level === 'agency' && (
                            <Text size="xs" c="dimmed">
                              their agency&apos;s share
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>{formatDate(w.businessDate)}</Table.Td>
                        <Table.Td>
                          {w.tabName ?? (w.orderNumber ? `Order ${w.orderNumber}` : '—')}
                          {w.checkNumber > 1 && (
                            <Text size="xs" c="dimmed">
                              bill {w.checkNumber}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {formatMoney(w.baseMinor, w.baseCurrency)}
                          <Text size="xs" c="dimmed">
                            {BASIS[w.basis]}
                            {w.fxRateMicro ? ` · at ${formatFxRate(w.fxRateMicro)}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {w.rateBp > 0 ? formatRateBp(w.rateBp) : 'a set amount'}
                          {w.tierApplied && (
                            <Text size="xs" c="teal">
                              raised by their volume
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td ta="right" fw={600}>
                          {formatMoney(w.amountMinor, w.currency)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}

            {run.skipped.length > 0 && (
              <Alert color="orange" variant="light" title="Some tables earned nothing">
                <Stack gap={4}>
                  {run.skipped.map((s) => (
                    <Text key={`${s.orderId}-${s.reason}`} size="sm">
                      <strong>{s.profileName}</strong> · {formatDate(s.businessDate)} · {s.tabName ?? 'a tab'}{' '}
                      — {WHY[s.reason]}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            )}

            {run.committed && (
              <Text size="sm" c="dimmed">
                Saved. Nobody has been paid yet — open Payouts to hand the money over.
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
